import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Package, Loader2, Store, BadgeCheck } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import SignatureCollection from "@/components/SignatureCollection";

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/connect/products").then(({ data }) => setProducts(data)).catch(() => {}),
      api.get("/connect/accounts").then(({ data }) => setAccounts(data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const buy = async (id) => {
    try {
      setBuying(id);
      const { data } = await api.post("/connect/checkout", { product_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBuying(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
      <div className="max-w-2xl">
        <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Marketplace</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold">Classes, events & gear.</h1>
        <p className="mt-4 text-neutral-400">Buy directly from Barbistry educators and vendors. Secure hosted checkout — your payment is split automatically to the seller.</p>
      </div>

      <div className="mt-12 border-t border-white/10 pt-12">
        <SignatureCollection />
      </div>

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
      ) : (
        <>
          {/* products storefront */}
          {products.length === 0 ? (
            <div className="py-20 text-center text-neutral-500" data-testid="market-empty">No products listed yet. Check back soon.</div>
          ) : (
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => (
                <div key={p.id} data-testid={`product-${p.id}`} className="bg-[#111] border border-white/10 overflow-hidden flex flex-col hover:border-[#D4AF37]/40 transition-colors">
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="h-44 w-full object-cover" />
                  ) : (
                    <div className="h-44 w-full bg-[#0a0a0a] flex items-center justify-center"><Package className="text-neutral-700" size={40} /></div>
                  )}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-display text-lg font-semibold">{p.name}</h3>
                    <p className="text-xs text-neutral-500">by {p.owner_name}</p>
                    <p className="mt-2 text-sm text-neutral-400 line-clamp-2 flex-1">{p.description}</p>
                    <div className="mt-4 flex items-center justify-between">
                      <span className="font-display text-2xl font-bold gold-text">${(p.price_cents / 100).toFixed(2)}</span>
                      <Button onClick={() => buy(p.id)} disabled={buying === p.id} data-testid={`buy-${p.id}`}
                        className="rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold">
                        {buying === p.id ? "…" : "Buy"}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* connected accounts (sellers) */}
          {accounts.length > 0 && (
            <div className="mt-20">
              <div className="flex items-center gap-2 text-[#D4AF37] mb-5"><Store size={16} /><span className="font-mono text-xs uppercase tracking-wider">Connected Sellers</span></div>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {accounts.map((a) => (
                  <div key={a.account_id} data-testid={`seller-${a.account_id}`} className="flex items-center gap-3 bg-[#0a0a0a] border border-white/10 p-4">
                    <div className="w-10 h-10 bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-display font-bold">{(a.name || "S")[0]}</div>
                    <div className="min-w-0">
                      <p className="font-semibold truncate flex items-center gap-1.5">{a.name} <BadgeCheck size={14} className="text-[#D4AF37]" /></p>
                      <p className="text-xs text-neutral-500 capitalize">{a.role} · {a.account_id.slice(0, 14)}…</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
